import React, { useState, useEffect } from 'react';
import { Dashboard } from './components/Dashboard';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { WalletTransfer } from './components/WalletTransfer';
import { Staking } from './components/Staking';
import { Events } from './components/Events';
import { SplashScreen } from './components/SplashScreen';
import { PWAInstaller } from './components/PWAInstaller';
import { Toaster } from './components/ui/sonner';
import { Home, Wallet, Calendar, Send, TrendingUp, Gift } from 'lucide-react';
import { Rewards } from './components/Rewards';
import { Settings } from './components/Settings';
import WalletTest from './components/WalletTest';
import { AdminPanel } from './components/AdminPanel';
import { useSession } from './hooks/useSession';
import { useWalletData } from './hooks/useWalletData';
import { i18n } from './utils/i18n';
import { getDefaultDarkMode } from './utils/brand';

function readDarkMode(): boolean {
  try {
    const saved = localStorage.getItem('vatton-dark-mode');
    if (saved !== null) return JSON.parse(saved);
  } catch {
    /* ignore */
  }
  return getDefaultDarkMode();
}

// ===== 타입 정의 =====
export interface User {
  id: number;
  nickname: string;
  walletAddress: string;
  referralCode: string;
  referredBy: string | null;
  balance: number;
  createdAt: string;
}

export interface Transaction {
  id?: number;
  type?: string;
  amount?: number;
  createdAt?: string;
  // 블록체인 조회 시 사용되는 필드
  hash?: string;
  from?: string | null;
  to?: string | null;
  value?: string;
  blockNumber?: number;
  timestamp?: number;
  status?: string;
  source?: string;
}

export default function App() {
  // ===== RPC API 훅 사용 =====
  const { user, accessToken, isLoading: sessionLoading, login, signup, logout } = useSession();
  const { avaxBalance, erc20Balances, vttnBalance, transactions, isLoading: walletLoading, refresh } = useWalletData(user?.walletAddress);
  
  // ===== 상태 관리 =====
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState('login');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isAdmin, setIsAdmin] = useState(false);
  const [eventSettings, setEventSettings] = useState({
    startDate: '2024-12-25T20:00:00',
    endDate: '2024-12-26T08:00:00'
  });
  const [darkMode, setDarkMode] = useState(readDarkMode);
  const darkModeBootRef = React.useRef(true);
  
  // PWA 설치 상태
  const [showSplash, setShowSplash] = useState(true);
  const [showPWAInstaller, setShowPWAInstaller] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);

  // ===== SSE 연결 관리 =====
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const token = localStorage.getItem('vatton-jwt-token');
    if (!token) return;

    // Mock SSE 연결 (실제 환경에서는 실제 SSE 사용)
    const mockSSE = setInterval(() => {
      // 모의 실시간 업데이트
    }, 30000);

    return () => {
      clearInterval(mockSSE);
    };
  }, [isAuthenticated, user]);

  // ===== 스플래시 화면 및 PWA 설치 관리 =====
  useEffect(() => {
    // PWA 설치 상태 체크
    const pwaInstalled = localStorage.getItem('vatton-pwa-installed');
    
    if (pwaInstalled) {
      setIsPWAInstalled(true);
      setShowSplash(false);
      setShowPWAInstaller(false);
    } else {
      // 스플래시 화면 타이머
      const splashTimer = setTimeout(() => {
        setShowSplash(false);
        setShowPWAInstaller(true);
      }, 3000);

      return () => clearTimeout(splashTimer);
    }
  }, []);

  // ===== 다크 모드 초기화 및 자동 로그인 체크 =====
  useEffect(() => {
    if (!isPWAInstalled && (showSplash || showPWAInstaller)) return;

    // RPC API를 통한 자동 로그인 체크
    if (accessToken && user) {
      setIsAuthenticated(true);
      setCurrentView('dashboard');
    }
  }, [isPWAInstalled, showSplash, showPWAInstaller, accessToken, user]);

  // ===== 다크 모드 효과 적용 =====
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    if (darkModeBootRef.current) {
      darkModeBootRef.current = false;
      return;
    }
    localStorage.setItem('vatton-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);


  // ===== 인증 함수들 =====
  const handleLogin = async (nickname: string, pin: string) => {
    try {
      const result = await login(nickname, pin);
      
      if (result.success) {
        setIsAuthenticated(true);
        setCurrentView('dashboard');
        setActiveTab('dashboard');
        return { success: true };
      } else {
        throw new Error(result.error || '로그인 실패');
      }
    } catch (error) {
      console.error('로그인 오류:', error);
      throw error;
    }
  };

  const handleRegister = async (nickname: string, pin: string, phoneNumber?: string) => {
    setIsLoading(true);

    try {
      // 입력값 검증
      if (!nickname.trim() || !pin.trim()) {
        throw new Error('Please enter both nickname and PIN.');
      }

      if (pin.length < 6) {
        throw new Error('PIN은 최소 6자리여야 합니다.');
      }

      console.log('Attempting register with:', { 
        nickname: nickname.trim(), 
        pin: pin.trim(), 
        phoneNumber: phoneNumber?.trim()
      }); // 디버깅용

      const result = await signup(nickname.trim(), pin.trim(), phoneNumber?.trim());

      console.log('Register response:', result); // 디버깅용

      if (result.success) {
        // JWT 토큰 저장
        if (result.accessToken) {
          localStorage.setItem('vatton-jwt-token', result.accessToken);
        }
        if (result.refreshToken) {
          localStorage.setItem('vatton-refresh-token', result.refreshToken);
        }
        
        // 회원가입 성공 후 바로 대시보드로 이동
        setIsAuthenticated(true);
        setCurrentView('dashboard');
        setActiveTab('dashboard');
        return { success: true, message: '회원가입이 완료되었습니다.' };
      } else {
        throw new Error(result.error || 'Registration failed.');
      }
    } catch (error) {
      console.error('회원가입 오류:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    console.log('Logging out user'); // 디버깅용
    
    // 로그아웃 처리
    logout();
    
    // 상태 초기화
    setIsAuthenticated(false);
    setCurrentView('login');
    setActiveTab('dashboard');
    setIsLoading(false);
    
    console.log('Logout completed'); // 디버깅용
  };

  // ===== API base 설정 =====
  const rawApiBase = (import.meta as any)?.env?.VITE_API_URL ?? 'https://wallet.vatton.app';
  const apiBase = rawApiBase.endsWith('/') ? rawApiBase.slice(0, -1) : rawApiBase;

  // ===== 지갑 함수들 =====
  const updateBalance = async (newBalance: number) => {
    try {
      // vatton-jwt-token 또는 accessToken 사용
      const token = localStorage.getItem('vatton-jwt-token') || accessToken;
      if (!token) {
        // 토큰이 없어도 로컬 상태만 업데이트 (에러 로그 제거)
        return;
      }

      const response = await fetch(`${apiBase}/api/vatton/users/balance`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ balance: newBalance })
      });

      if (!response.ok) {
        throw new Error('잔고 업데이트 실패');
      }

      const result = await response.json();
      console.log('잔고 업데이트 성공:', result);
    } catch (error) {
      console.error('잔고 업데이트 오류:', error);
    }
  };

  const addTransaction = async (transaction: Omit<Transaction, 'id'>) => {
    try {
      // vatton-jwt-token 또는 accessToken 사용
      const token = localStorage.getItem('vatton-jwt-token') || accessToken;
      if (!token) {
        // 토큰이 없어도 로컬 상태만 업데이트 (에러 로그 제거)
        return;
      }

      const response = await fetch(`${apiBase}/api/vatton/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transaction)
      });

      if (!response.ok) {
        throw new Error('트랜잭션 추가 실패');
      }

      const result = await response.json();
      console.log('트랜잭션 추가 성공:', result);
      return result;
    } catch (error) {
      console.error('트랜잭션 추가 오류:', error);
    }
  };

  // ===== 관리자 함수들 =====
  const handleAdminLogin = (password: string) => {
    if (password === '3bebe@MAMA') {
      setIsAdmin(true);
      return true;
    }
    return false;
  };

  const handleEventSettingsUpdate = (settings: typeof eventSettings) => {
    setEventSettings(settings);
  };

  // ===== 다크 모드 토글 =====
  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  // ===== PWA 설치 핸들러 =====
  const handlePWAInstallComplete = () => {
    localStorage.setItem('vatton-pwa-installed', 'true');
    setIsPWAInstalled(true);
    setShowPWAInstaller(false);
  };

  // ===== 렌더링 =====
  // 어드민 패널 (숨김 URL: /vattonadmin)
  if (window.location.pathname === '/vattonadmin') {
    return (
      <>
        <AdminPanel />
        <Toaster />
      </>
    );
  }

  // 스플래시 화면
  if (showSplash) {
    return <SplashScreen />;
  }

  // PWA 설치 화면
  if (showPWAInstaller) {
    return <PWAInstaller onInstallComplete={handlePWAInstallComplete} />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="min-h-screen bg-background">
          {/* Mobile Frame - Full screen for PWA */}
          <div className="w-full min-h-screen bg-background relative">
            {/* Content */}
            <div className="h-full overflow-auto scrollbar-hide">
              {currentView === 'login' ? (
                <Login 
                  onLogin={handleLogin}
                  onSwitchToRegister={() => setCurrentView('register')}
                  isLoading={sessionLoading}
                />
              ) : (
                <Register 
                  onRegister={handleRegister}
                  onSwitchToLogin={() => setCurrentView('login')}
                  isLoading={sessionLoading}
                />
              )}
            </div>
          </div>
        </div>
        <Toaster />
      </>
    );
  }

  // 세션 로딩 중일 때만 로딩 화면 표시 (user가 없어도 메뉴 접근 허용)
  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">사용자 정보를 불러오는 중입니다...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        {/* Mobile Frame - Full screen for PWA */}
        <div className="w-full min-h-screen bg-background relative" style={{ maxWidth: '100vw', height: '100vh' }}>
          {/* Content */}
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-auto scrollbar-hide" style={{ paddingBottom: '80px' }}>
              {activeTab === 'dashboard' && (
                <Dashboard 
                  user={user} 
                  transactions={transactions}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                  onNavigateToNews={() => setActiveTab('events')}
                  onNavigateToSettings={() => setActiveTab('settings')}
                  onNavigateToEvents={() => setActiveTab('events')}
                />
              )}
              {activeTab === 'transfer' && (
                <WalletTransfer 
                  user={user}
                  transactions={transactions}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                />
              )}
              {activeTab === 'staking' && (
                <Staking 
                  user={user}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                />
              )}
              {activeTab === 'events' && (
                <Events 
                  user={user}
                  onUpdateBalance={updateBalance}
                  onAddTransaction={addTransaction}
                  eventSettings={eventSettings}
                  onTabChange={setActiveTab}
                />
              )}
              {activeTab === 'rewards' && (
                <Rewards 
                  user={user}
                  onSettingsClick={() => setActiveTab('settings')}
                />
              )}
              {activeTab === 'settings' && (
                <Settings 
                  user={user}
                  onLogout={handleLogout}
                  darkMode={darkMode}
                  onToggleDarkMode={setDarkMode}
                  isAdmin={false}
                  onAdminLogin={(password: string) => false}
                  eventSettings={eventSettings}
                  onEventSettingsUpdate={handleEventSettingsUpdate}
                />
              )}

            </div>

            {/* Bottom Navigation - Fixed */}
            <div className="fixed bottom-0 left-0 right-0 h-20 bg-background border-t border-border flex items-center justify-around px-2 z-50" style={{ maxWidth: '100vw' }}>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'dashboard' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Home size={18} />
                <span className="text-xs">{i18n.t('home')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('transfer')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'transfer' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Wallet size={18} />
                <span className="text-xs">{i18n.t('wallet')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('staking')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'staking' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <TrendingUp size={18} />
                <span className="text-xs">{i18n.t('staking')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('events')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'events' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Calendar size={18} />
                <span className="text-xs">{i18n.t('events')}</span>
              </button>
              
              <button
                onClick={() => setActiveTab('rewards')}
                className={`flex flex-col items-center gap-1 py-2 px-2 transition-colors duration-200 ${
                  activeTab === 'rewards' 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                <Gift size={18} />
                <span className="text-xs">{i18n.t('rewards')}</span>
              </button>
              
            </div>
          </div>
        </div>
      </div>
      
      
      <Toaster />
    </>
  );
}